/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import {
    Text,
    Box,
    Heading,
    SimpleGrid,
    List,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {useProductList} from '../util/product-list-context'

// Custom components
import Search from '../../../components/search'
import MetaTagsComponent from '../../../components/metatags'
import LoadingSpinner from '../../../components/loading-spinner'
import RecommendedProducts from '../../../components/recommended-products'
import useSiteType from '../../../hooks/use-site-type'

// OOTB Components
import {EINSTEIN_RECOMMENDERS} from '../../../constants'

// Page designer part
import {usePage} from '@salesforce/commerce-sdk-react'
import {
    PDCLink,
    PDCHomeCategoriesPushTile,
    PDCHomeServicesPushTile
} from '../../../page-designer/assets'
import {PDCHomeCategoriesPush, PDCHomeServicesPush} from '../../../page-designer/layouts'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {getPageDesignerRegion} from '../../../utils/utils'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcDisplayConfigLink': PDCLink,
    'commerce_assets.pdcHomeCategoriesPushTile': PDCHomeCategoriesPushTile,
    'commerce_assets.pdcHomeServicesPushTile': PDCHomeServicesPushTile,
    'commerce_layouts.pdcHomeCategoriesPush': PDCHomeCategoriesPush,
    'commerce_layouts.pdcHomeServicesPush': PDCHomeServicesPush
}

/**
 * A React functional component that renders a user interface for displaying
 * empty search results. This component provides users with helpful tips,
 * common searches, and assistance links when no search results are found.
 * It also displays recommended products and additional components based on
 * the page design configuration.
 *
 * @param {Object} props - The component properties.
 * @param {string} props.searchQuery - The search query entered by the user.
 * @param {Object} props.category - The category object containing information
 * about the current category context.
 *
 * @returns {JSX.Element} A JSX element representing the empty search results
 * page.
 */
const EmptySearchResults = ({searchQuery, category}) => {
    const {isProductListLoading, metatags} = useProductList()

    const search = isProductListLoading ? '' : category?.name || searchQuery

    // Get empty search PD components
    const {isOutlet} = useSiteType()
    const {data: emptySearchPage} = usePage({parameters: {pageId: 'search-no-result-page' + (isOutlet ? '-outlet' : '')}})

    // main region components
    const mainRegion = getPageDesignerRegion(emptySearchPage, 'main')
    const categoriesPushComponent = mainRegion?.components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcHomeCategoriesPush'
    )
    const servicesPushComponent = mainRegion?.components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcHomeServicesPush'
    )
    const isPDComponents = categoriesPushComponent || servicesPushComponent

    // common searches links from common_searches region
    const commonSearchLinks = emptySearchPage?.regions?.[1].components

    // help links from help_section region
    const helpLinks = emptySearchPage?.regions?.[2].components

    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    const finalMetadata = [
        {
            id: 'description',
            value:
                category?.pageDescription ||
                metatags?.find((tag) => tag.id === 'description')?.value ||
                searchQuery
        },
        {
            id: 'title',
            value:
                category?.pageTitle ||
                metatags?.find((tag) => tag.id === 'title')?.value ||
                searchQuery
        },
        {
            id: 'keywords',
            value:
                category?.pageKeywords ||
                metatags?.find((tag) => tag.id === 'keywords')?.value ||
                ''
        },
        {
            id: 'robots',
            value: 'noindex'
        }
    ]

    return (
        <Box borderTopWidth={1} borderColor="borderGray">
            <MetaTagsComponent metaData={finalMetadata} />
            <Box
                px={{base: '16px', lg: '30px'}}
                pt="64px"
                pb="56px"
                maxWidth={{base: 'none', md: '796px'}}
                mx={{base: '0', md: 'auto'}}
            >
                {isProductListLoading && (
                    <LoadingSpinner isBodyLocked={true} wrapperStyles={{height: '100dvh'}} />
                )}

                <Heading
                    as="h1"
                    fontSize="4xl"
                    textTransform="uppercase"
                    mb={{base: '40px', md: '52px'}}
                    textAlign={{base: 'left', md: 'center'}}
                >
                    <FormattedMessage
                        defaultMessage='We couldn’t find anything for "{search}"'
                        id="empty_search_results.main_heading"
                        values={{search}}
                    />
                </Heading>

                <Search
                    isSuggestionsEnabled={false}
                    isFocusOnInit={false}
                    containerProps={{
                        maxWidth: '412px',
                        mx: 'auto'
                    }}
                />

                <SimpleGrid
                    columns={{base: 1, md: 3}}
                    spacing="40px"
                    mt={{base: '40px', lg: '52px'}}
                >
                    {/* Column 1: Our Tips */}
                    <Box>
                        <Text as="h2" variant="eyeBrowSmall" mb="8px" textTransform="uppercase">
                            <FormattedMessage
                                defaultMessage="Our Tips"
                                id="empty_search_results.our_tips.title"
                            />
                        </Text>

                        <List listStyleType="disc" paddingInlineStart="24px" spacing="8px">
                            <ListItem textStyle="bodyLarge2">
                                <FormattedMessage
                                    defaultMessage="Check your spelling"
                                    id="empty_search_results.our_tips.tip_1"
                                />
                            </ListItem>
                            <ListItem textStyle="bodyLarge2">
                                <FormattedMessage
                                    defaultMessage="Be more generic"
                                    id="empty_search_results.our_tips.tip_2"
                                />
                            </ListItem>
                            <ListItem textStyle="bodyLarge2">
                                <FormattedMessage
                                    defaultMessage="Try similar terms"
                                    id="empty_search_results.our_tips.tip_3"
                                />
                            </ListItem>
                        </List>
                    </Box>

                    {/* Column 2: Common Searches */}
                    <Box>
                        <Text as="h2" variant="eyeBrowSmall" mb="16px" textTransform="uppercase">
                            <FormattedMessage
                                defaultMessage="Common Searches"
                                id="empty_search_results.common_searches.title"
                            />
                        </Text>

                        {commonSearchLinks && (
                            <List spacing="16px">
                                {commonSearchLinks.map((item) => (
                                    <ListItem key={item.id}>
                                        <PDCLink custom={item?.custom} label={item?.data?.label} />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                    {/* Column 3: How We Can Help */}
                    <Box>
                        <Text as="h2" variant="eyeBrowSmall" mb="16px" textTransform="uppercase">
                            <FormattedMessage
                                defaultMessage="How can we help?"
                                id="empty_search_results.help.title"
                            />
                        </Text>
                        {helpLinks && (
                            <List spacing="16px">
                                {helpLinks.map((item) => (
                                    <ListItem key={item.id}>
                                        <PDCLink
                                            displayConfig="transparent button"
                                            custom={item?.custom}
                                            label={item?.data?.label}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </SimpleGrid>
            </Box>

            {/* Render PD component in order: categoriesPushComponent && servicesPushComponent */}
            {isPDComponents && (
                <PageContext.Provider value={contextValue}>
                    {categoriesPushComponent && <Component component={categoriesPushComponent} />}
                    {servicesPushComponent && <Component component={servicesPushComponent} />}
                </PageContext.Provider>
            )}

            <RecommendedProducts
                title={
                    <Heading
                        as="h2"
                        size={['3xl', null, '4xl']}
                        pb={[40, null, 0]}
                        pl={['10px', null, 0]}
                    >
                        <FormattedMessage
                            defaultMessage="TRENDING PRODUCTS"
                            id="empty_search_results.recommended_products.title.top_sellers"
                        />
                    </Heading>
                }
                recommender={EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_TOP_SELLERS}
                isScroller={false}
                isCarousel={{base: false, md: true, lg: true}}
                mb={{base: '60px', lg: '120px'}}
            />
        </Box>
    )
}

EmptySearchResults.propTypes = {
    searchQuery: PropTypes.string,
    category: PropTypes.object
}

export default EmptySearchResults
